import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Box,
  OutlinedInput,
  CircularProgress,
  Chip,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";

import api from "../../../api/api";
import { setAlert } from "../../../actions/alert";

import { convertToTitleCase } from "../../../utils/recipe";

const Searchable = ({
  label = "Search",
  helperText = "",
  error = false,
  multiple = true,
  maxItems = 10,
  freeSolo = true,
  unique = true,
  onItemChange,
  apiUrl,
  fullWidth = true,
  setAlert,
  selectedItemsFromParent, //Prefill value
  disabled = false,
  maxInputLength = 128,
  convertToTitleCaseOnLoad = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [inputFocused, setInputFocused] = useState(false);
  const [lastFiredInputValue, setLastFiredInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  const searchFunction = async (inputValue) => {
    try {
      if (inputValue === "") {
        return [];
      }

      const res = await api.get(`${apiUrl}/${inputValue}`);
      return res.data;
    } catch (err) {
      return [];
    }
  };

  const handleInputChange = (e, value) => {
    if (value) {
      setInputValue(value);
    } else {
      setInputValue("");
    }
  };

  const handleBlur = () => {
    setInputFocused(false);

    // If multiple is false and there's an input value, submit it as an item
    if (!multiple && inputValue && selectedItems && inputValue !== lastFiredInputValue.trim()) {
      //Check to see if input value is already in selectedItems
      if (selectedItems.length > 0 && typeof selectedItems === "object") {
        if (selectedItems.some((item) => item.value === inputValue.trim())) {
          return;
        }
      } else {
        if (selectedItems.value === inputValue.trim()) {
          return;
        }
      }

      const newItem = {
        value: inputValue.trim(),
        name: inputValue.trim(),
      };

      setSelectedItems(newItem);
      // Fire onItemChange with the new item
      if (onItemChange) {
        onItemChange(newItem);
        setLastFiredInputValue(inputValue.trim());
      }
    }
  };

  useEffect(() => {
    if (selectedItemsFromParent !== selectedItems) {
      if (selectedItemsFromParent) {
        if (Array.isArray(selectedItemsFromParent)) {
          // Handle array case
          setSelectedItems(selectedItemsFromParent);
          setInputValue("");
        } else if (typeof selectedItemsFromParent === "object" && selectedItemsFromParent !== null) {
          // Handle object case
          setSelectedItems(selectedItemsFromParent);
          if (selectedItemsFromParent?.value) {
            setInputValue(
              convertToTitleCaseOnLoad
                ? convertToTitleCase(selectedItemsFromParent.value)
                : selectedItemsFromParent.value
            );
          } else {
            setInputValue("");
          }
        } else if (typeof selectedItemsFromParent === "string") {
          if (convertToTitleCaseOnLoad) {
            setSelectedItems({
              name: convertToTitleCase(selectedItemsFromParent),
              value: convertToTitleCase(selectedItemsFromParent),
            });
          } else {
            setSelectedItems({ name: selectedItemsFromParent, value: selectedItemsFromParent });
          }
          setInputValue(selectedItemsFromParent);
        } else {
          // Handle other cases (e.g., null, undefined)
          setSelectedItems([]);
          setInputValue("");
        }
      } else {
        setSelectedItems([]);
        setInputValue("");
        setLastFiredInputValue("");
        setOptions([]);
      }
    }
  }, [selectedItemsFromParent, selectedItems]);

  useEffect(() => {
    setLoading(true);
    searchFunction(inputValue)
      .then((newOptions) => {
        setOptions(newOptions);
        setLoading(false);
      })
      .catch(() => {
        setOptions([]);
        setLoading(false);
      });
  }, [inputValue]);

  return (
    <div>
      <Autocomplete
        freeSolo={freeSolo}
        multiple={multiple}
        options={options}
        getOptionLabel={(option) => {
          if (typeof option === "string") {
            return option;
          } else if (option && option.name) {
            return option.name;
          } else {
            return "";
          }
        }}
        fullWidth={fullWidth}
        onInputChange={handleInputChange}
        disabled={disabled}
        error={error}
        onChange={(event, newValue) => {
          // If multiple is true, newValue is an array
          if (multiple) {
            newValue = newValue
              .map((item) => {
                if (typeof item === "string") {
                  item = item.trim();
                  if (item === "") {
                    return null;
                  }
                  return { value: item, name: item };
                }
                return item;
              })
              .filter((item) => item !== null);

            // Clamp the newValue to maxItems
            if (newValue.length > maxItems) {
              setAlert(`You can only select up to ${maxItems} items.`, "error");
              newValue = newValue.slice(0, maxItems);
            }
          } else {
            // If multiple is false, newValue is a single value
            if (typeof newValue === "string") {
              newValue = newValue.trim();
              if (newValue !== "") {
                newValue = { value: newValue, name: newValue };
              } else {
                newValue = null;
              }
            }
          }

          // Update the state
          setSelectedItems(newValue);

          // Call the onChange function passed as a prop
          onItemChange(newValue);
        }}
        noOptionsText={`Search for an item...`}
        classes={{
          paper: "bg-white",
        }}
        filterOptions={(options, { inputValue }) => {
          let isExactMatch = options.some((option) => option.name.toLowerCase() === inputValue.toLowerCase());

          // Filter out the already selected options
          if (unique && multiple) {
            options = options.filter((option) => {
              return !selectedItems.some((selectedItem) => selectedItem.value === option.value);
            });
          }

          if (inputValue !== "" && !isExactMatch) {
            options = [
              ...options,
              {
                value: `${inputValue}`,
                name: `${inputValue}`,
                isCustom: true,
              },
            ];
          }

          return options;
        }}
        renderOption={(props, option, { selected }) => (
          <li {...props} key={option.name} className="cursor-pointer px-4 my-[0.375rem] w-full">
            <div className="flex flex-row items-start gap-2 w-full min-w-0">
              {option.isCustom && (
                <div className="flex items-center justify-center w-4 h-4 rounded-full bg-mainGreen flex-shrink-0 my-[0.375rem]">
                  <Typography className="text-white text-sm font-medium">+</Typography>
                </div>
              )}
              <div className="min-w-0 flex-1">
                <Typography
                  variant="subtitle1"
                  className={`font-normal break-words whitespace-normal overflow-hidden ${
                    option.isCustom ? "italic text-mainGreen-400" : "normal text-inherit"
                  }`}
                >
                  {option.isCustom ? `Add "${option.name}"` : option.name}
                </Typography>
              </div>
            </div>
          </li>
        )}
        renderTags={(value, getTagProps) =>
          multiple
            ? value.slice(0, maxItems).map((option, index) => {
                // Convert strings to objects
                if (typeof option === "string") {
                  option = { value: option, name: option };
                }

                return <Chip variant="outlined" label={option.name} key={option.key} {...getTagProps({ index })} />;
              })
            : value.map((option, index) => {
                // Convert strings to objects
                if (typeof option === "string") {
                  option = { value: option, name: option };
                }

                return <Chip variant="outlined" label={option.name} key={option.key} {...getTagProps({ index })} />;
              })
        }
        renderInput={(params) => (
          <FormControl fullWidth variant="outlined">
            <TextField
              {...params}
              id="custom-autocomplete"
              helperText={helperText}
              variant="outlined"
              onFocus={() => setInputFocused(true)}
              onBlur={() => handleBlur()}
              label={label}
              error={error}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              inputProps={{
                ...params.inputProps,
                value: inputValue,
                maxLength: maxInputLength,
              }}
            />
          </FormControl>
        )}
        value={selectedItems}
      />
    </div>
  );
};

Searchable.propTypes = {
  label: PropTypes.string.isRequired,
  helperText: PropTypes.string,
  searchFunction: PropTypes.func.isRequired,
  selectFunction: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  maxItems: PropTypes.number,
  freeSolo: PropTypes.bool,
  unique: PropTypes.bool,
  apiUrl: PropTypes.string.isRequired,
  fullWidth: PropTypes.bool,
  setAlert: PropTypes.func.isRequired,
  selectedItemsFromParent: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};

const mapStateToProps = (state) => ({});

export default connect(mapStateToProps, { setAlert })(Searchable);
